import { useRequiredContext } from "@redotech/react-util/context";
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";

export type EscalationStatus = "idle" | "waiting" | "human-active" | "closed";

/**
 * Health of the live SSE watcher. `disconnected` = fast retries exhausted,
 * still retrying slowly in the background. Absent ≈ connected.
 */
export type EscalationConnectionState =
  | "connected"
  | "reconnecting"
  | "disconnected";

export interface EscalationState {
  escalationId: string | null;
  intercomConversationId: string | null;
  noticeTopic: string | null;
  status: EscalationStatus;
  /**
   * The triage thread id this escalation is bound to. Captured at
   * escalation creation / hydration time and never overwritten when
   * the merchant opens a different thread — that's the bug it
   * defends against: `setTriageThreadId` is the "currently displayed
   * thread" setter, which gets clobbered on every history click. If
   * the watcher used that field to route live human messages, opening
   * an unrelated thread mid-escalation would land the admin's next
   * reply on the wrong cached chat. Use this stable id instead for
   * any escalation-bound dispatch.
   */
  triageThreadId: string | null;
  /**
   * Newest admin/human reply time (epoch ms), stamped server-side by
   * the Intercom webhook. Compared against `lastReadAt` to derive
   * "unread" — drives the notification toast (and, later, a launcher
   * red dot). Null when no admin reply has landed yet.
   */
  lastAdminMessageAt: number | null;
  /**
   * When the merchant last opened/read this escalated thread (epoch
   * ms). Unread when `lastAdminMessageAt > lastReadAt` (or unset).
   */
  lastReadAt: number | null;
  /** Live SSE watcher health for the bound escalation. */
  connectionState?: EscalationConnectionState;
}

/**
 * One entry per concurrently-active escalation, keyed by escalationId.
 * The notification pipeline (SSE streams + toasts) fans out over this.
 */
export interface ActiveEscalationSummary {
  escalationId: string;
  triageThreadId: string;
  status: Exclude<EscalationStatus, "idle">;
  lastAdminMessageAt: number | null;
  lastReadAt: number | null;
}

export interface EscalationContextType {
  /** The currently viewed/bound escalation (panel UI, send diversion). */
  escalation: EscalationState;
  setEscalation: (escalation: Partial<EscalationState>) => void;
  clearEscalation: () => void;
  activeEscalations: ReadonlyMap<string, ActiveEscalationSummary>;
  setActiveEscalations: (summaries: ActiveEscalationSummary[]) => void;
  upsertActiveEscalation: (summary: ActiveEscalationSummary) => void;
  updateActiveEscalation: (
    escalationId: string,
    update: Partial<Omit<ActiveEscalationSummary, "escalationId">>,
  ) => void;
  removeActiveEscalation: (escalationId: string) => void;
  triageThreadId: string | null;
  setTriageThreadId: (id: string | null) => void;
  navigateToThread: ((threadId: string) => void) | null;
  setNavigateToThread: (fn: ((threadId: string) => void) | null) => void;
}

const defaultState: EscalationState = {
  escalationId: null,
  intercomConversationId: null,
  noticeTopic: null,
  status: "idle",
  triageThreadId: null,
  lastAdminMessageAt: null,
  lastReadAt: null,
};

/**
 * Derive "the merchant has an unread support reply" from the read-state
 * timestamps. Shared by the notification toast and (later) the launcher
 * red dot so both agree on what "unread" means.
 */
export function escalationHasUnread(
  state: Pick<EscalationState, "lastAdminMessageAt" | "lastReadAt">,
): boolean {
  return (
    state.lastAdminMessageAt != null &&
    (state.lastReadAt == null || state.lastAdminMessageAt > state.lastReadAt)
  );
}

/** A live handoff: merchant queued for or talking to a human. */
export function isActiveHandoffStatus(
  status: EscalationStatus,
): status is "waiting" | "human-active" {
  return status === "waiting" || status === "human-active";
}

/**
 * Whether the AI "thinking" indicator should show. Suppressed during a
 * live handoff so a synthetic "submitted" status can't render a spinner.
 */
export function shouldShowThinkingIndicator(args: {
  isStreaming: boolean;
  isEscalatedConversation: boolean;
  escalationStatus: EscalationStatus;
}): boolean {
  const { isStreaming, isEscalatedConversation, escalationStatus } = args;
  return (
    isStreaming &&
    !(isEscalatedConversation && isActiveHandoffStatus(escalationStatus))
  );
}

/**
 * Map server-reported active escalations to context summaries,
 * dropping anything that no longer needs a live stream.
 */
export function toActiveEscalationSummaries(
  actives: {
    escalationId: string;
    triageThreadId: string;
    status: EscalationStatus;
    lastAdminMessageAt: number | null;
    lastReadAt: number | null;
  }[],
): ActiveEscalationSummary[] {
  return actives.flatMap((active) =>
    isActiveHandoffStatus(active.status)
      ? [
          {
            escalationId: active.escalationId,
            triageThreadId: active.triageThreadId,
            status: active.status,
            lastAdminMessageAt: active.lastAdminMessageAt,
            lastReadAt: active.lastReadAt,
          },
        ]
      : [],
  );
}

export const EscalationContext = createContext<
  EscalationContextType | undefined
>(undefined);

export function useEscalationContext(): EscalationContextType {
  return useRequiredContext(EscalationContext);
}

interface EscalationProviderProps {
  children: ReactNode;
}

export const EscalationProvider = memo(function EscalationProvider({
  children,
}: EscalationProviderProps) {
  const [escalation, setEscalationState] =
    useState<EscalationState>(defaultState);
  const [activeEscalations, setActiveEscalationsState] = useState<
    ReadonlyMap<string, ActiveEscalationSummary>
  >(new Map());
  const [triageThreadId, setTriageThreadIdState] = useState<string | null>(
    null,
  );
  const [navigateToThread, setNavigateToThreadState] = useState<
    ((threadId: string) => void) | null
  >(null);

  const setTriageThreadId = useCallback((id: string | null) => {
    setTriageThreadIdState(id);
  }, []);

  const setNavigateToThread = useCallback(
    (fn: ((threadId: string) => void) | null) => {
      setNavigateToThreadState(() => fn);
    },
    [],
  );

  const setEscalation = useCallback((update: Partial<EscalationState>) => {
    setEscalationState((prev) => {
      // connectionState describes the stream of the escalation it was
      // written for; rebinding to another escalation must not carry it over.
      const rebinding =
        update.escalationId !== undefined &&
        update.escalationId !== prev.escalationId;
      return rebinding
        ? { ...prev, connectionState: undefined, ...update }
        : { ...prev, ...update };
    });
  }, []);

  const clearEscalation = useCallback(() => {
    setEscalationState(defaultState);
  }, []);

  const setActiveEscalations = useCallback(
    (summaries: ActiveEscalationSummary[]) => {
      setActiveEscalationsState(
        new Map(summaries.map((s) => [s.escalationId, s])),
      );
    },
    [],
  );

  const upsertActiveEscalation = useCallback(
    (summary: ActiveEscalationSummary) => {
      setActiveEscalationsState((prev) => {
        const next = new Map(prev);
        next.set(summary.escalationId, summary);
        return next;
      });
    },
    [],
  );

  const updateActiveEscalation = useCallback(
    (
      escalationId: string,
      update: Partial<Omit<ActiveEscalationSummary, "escalationId">>,
    ) => {
      setActiveEscalationsState((prev) => {
        const existing = prev.get(escalationId);
        if (!existing) {
          return prev;
        }
        const next = new Map(prev);
        next.set(escalationId, { ...existing, ...update });
        return next;
      });
    },
    [],
  );

  const removeActiveEscalation = useCallback((escalationId: string) => {
    setActiveEscalationsState((prev) => {
      if (!prev.has(escalationId)) {
        return prev;
      }
      const next = new Map(prev);
      next.delete(escalationId);
      return next;
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      escalation,
      setEscalation,
      clearEscalation,
      activeEscalations,
      setActiveEscalations,
      upsertActiveEscalation,
      updateActiveEscalation,
      removeActiveEscalation,
      triageThreadId,
      setTriageThreadId,
      navigateToThread,
      setNavigateToThread,
    }),
    [
      escalation,
      setEscalation,
      clearEscalation,
      activeEscalations,
      setActiveEscalations,
      upsertActiveEscalation,
      updateActiveEscalation,
      removeActiveEscalation,
      triageThreadId,
      setTriageThreadId,
      navigateToThread,
      setNavigateToThread,
    ],
  );

  return (
    <EscalationContext.Provider value={contextValue}>
      {children}
    </EscalationContext.Provider>
  );
});
